import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useCms } from "@/lib/cms";
import "./ProcessSection.css";

const steps = [
  { n: "01", title: "문의", desc: "비즈니스 목표와 요구사항을 청취합니다." },
  { n: "02", title: "기획", desc: "UX 흐름과 데이터 구조를 함께 설계합니다." },
  { n: "03", title: "개발", desc: "프론트 · 백엔드 · AI를 통합 구축합니다." },
  { n: "04", title: "테스트", desc: "QA · 부하 · 시나리오 테스트를 수행합니다." },
  { n: "05", title: "오픈", desc: "운영 환경에 안정적으로 배포합니다." },
  { n: "06", title: "유지보수", desc: "장기 파트너로 운영을 함께합니다." },
];

export default function ProcessSection({ embed = false }) {
  const { cms } = useCms();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.85", "end 0.2"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="process"
      ref={sectionRef}
      data-testid="process-section"
      className={`ds-process-section relative isolate${
        embed ? " ds-process-section--embed" : " py-24 md:py-32"
      } px-6 md:px-12`}
    >
      <div className="absolute inset-0 -z-10 bg-grid-sm opacity-30" />
      <div className="ds-process-section__glow" aria-hidden="true" />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="ds-process-section__eyebrow-line" />
          <span className="ds-process-section__eyebrow-label">Process</span>
        </div>

        <h2
          data-testid="process-title"
          className="ds-process-section__title ds-cms-text font-display font-medium tracking-tight leading-[1.1] max-w-3xl mb-4 md:mb-6 whitespace-pre-line"
        >
          {cms.process_title}
        </h2>
        {cms.process_body && (
          <p
            data-testid="process-body"
            className="ds-process-section__body ds-cms-text text-sm max-w-2xl mb-12 md:mb-16 whitespace-pre-line"
          >
            {cms.process_body}
          </p>
        )}
        {!cms.process_body && <div className="mb-12 md:mb-16" />}

        <div className="relative">
          <div className="ds-process-section__track hidden lg:block" aria-hidden="true" />
          <motion.div
            style={{ scaleX: lineScale, transformOrigin: "0% 50%" }}
            className="ds-process-section__track-fill hidden lg:block"
          />

          <div className="ds-process-section__grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
                data-testid={`process-step-${i}`}
                className="relative group"
              >
                <div className="hidden lg:flex items-center justify-center w-6 h-6 mx-auto mb-6 relative z-10">
                  <span className="ds-process-section__dot-ring" />
                  <motion.span
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 + 0.2, type: "spring", stiffness: 200 }}
                    className="ds-process-section__dot-core"
                  />
                </div>

                <div className="ds-process-section__card h-full">
                  <div className="ds-process-section__card-index">{s.n}</div>
                  <div className="mt-4">
                    <div className="ds-process-section__card-title font-display">{s.title}</div>
                    <p className="ds-process-section__card-desc">{s.desc}</p>
                  </div>
                  <div className="ds-process-section__card-shine" aria-hidden="true" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
